import { useQuery, UseQueryOptions } from 'react-query';
import { Asset, ASSET_TYPE, Broadcast, QUERY_KEYS } from '@24i/nxg-sdk-photon/src';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { getContinueWatchingOffset } from '../continueWatching/useContinueWatchingQuery';
import { useFetchedAsset } from './useFetchedAssets';
import { useUserFetch } from '../user/useUserFetch';

const useAssetDetailsQuery = (
    {
        assetId,
        assetType,
        continueWatchingPlaylist,
        channelId,
        sectionLabel,
    }: {
        assetId?: string;
        assetType?: ASSET_TYPE;
        continueWatchingPlaylist?: Asset[];
        channelId?: string | null;
        sectionLabel?: string | null;
    },
    options?: UseQueryOptions<Asset | Broadcast>
) => {
    const isOptionEnabled = options?.enabled !== undefined ? options.enabled : true;
    const { fetch: fetchUser } = useUserFetch();
    const { fetchAsset } = useContentData();
    const localAsset = useFetchedAsset(assetId);

    const fetchDetails = async (): Promise<Asset | Broadcast> => {
        let cw;
        let asset: Asset;
        const userData = await fetchUser();

        if (!assetId || !assetType)
            throw Error('Asset id or type are not provided and it is required to fetch details.');

        if (userData && continueWatchingPlaylist) {
            cw = getContinueWatchingOffset(continueWatchingPlaylist, assetId);
        }

        try {
            const fetchedAsset: Asset = await fetchAsset({
                id: assetId,
                type: assetType,
                channelId,
            });

            asset = {
                ...fetchedAsset,
                ...cw,
                sectionLabel,
            };

            if (!asset) {
                throw Error(`No asset details found with id ${assetId} and type ${assetType}.`);
            }

            return asset;
        } catch (err) {
            throw Error(JSON.stringify(err)); // TODO: use typed Error
        }
    };

    return useQuery<Asset | Broadcast>([QUERY_KEYS.assets, assetId], fetchDetails, {
        ...options,
        initialData: localAsset,
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: !!assetId && !!assetType && isOptionEnabled,
    });
};

export default useAssetDetailsQuery;
